import React from 'react';

const CONTACT_EMAIL = 'krcilr@gmail.com';

const iconProps = {
  width: 26,
  height: 26,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
};

const icons = {
  gavel: (
    <svg {...iconProps}>
      <rect x="9" y="5.5" width="11" height="5" rx="1.5" transform="rotate(45 14.5 8)" />
      <path d="M12.7 9.8L4.5 18" />
      <rect x="13" y="19" width="8" height="2.5" rx="1" />
    </svg>
  ),
  chart: (
    <svg {...iconProps}>
      <path d="M4 20V4M4 20h16M8 16l4-5 3 3 5-7" />
    </svg>
  ),
  bracket: (
    <svg {...iconProps}>
      <path d="M2 4h5v5H2M2 15h5v5H2M7 6.5h5v11H7M12 12h5" />
      <circle cx="19.5" cy="12" r="2.5" />
    </svg>
  ),
  target: (
    <svg {...iconProps}>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="4" />
      <path d="M12 12l7-7M16 5h3v3" />
    </svg>
  ),
};

const projects = [
  {
    name: 'Team Auction',
    shortName: 'Team Auction',
    subdomain: 'teamauction',
    description: 'Fantasy team auctions with real-time bidding, up-to-date stats and league management in one place.',
    icon: 'gavel',
    features: ['Live auctions', 'Real-time bidding', 'Up-to-date stats'],
  },
  {
    name: 'Bettor Bot',
    shortName: 'Bettor Bot',
    subdomain: 'bettors',
    description: 'AI-powered betting analytics and predictions to back every wager with data, not gut feel.',
    icon: 'chart',
    features: ['AI predictions', 'Analytics dashboard', 'Betting insights'],
  },
  {
    name: 'Playoff Fantasy Football',
    shortName: 'Playoffs',
    subdomain: 'playoffs',
    description: 'Build a playoff roster and score points as your players advance through the postseason.',
    icon: 'bracket',
    features: ['Roster creation', 'Live scoring', 'Playoff bracket'],
  },
  {
    name: 'Draft Predictions',
    shortName: 'Draft',
    subdomain: 'draft',
    description: 'A live NFL Draft prediction pool. Take turns calling each pick in real time and win the rolling pot.',
    icon: 'target',
    features: ['Live pick predictions', 'Rotating turn order', 'Rolling pot'],
  },
];

const projectUrl = (project) => `https://${project.subdomain}.rosterfreak.com`;

const Logo = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" aria-hidden="true">
    <rect x="1" y="1" width="30" height="30" rx="9" fill="none" stroke="#7DF9C2" strokeWidth="2" />
    <path
      d="M10 22V10h7a4 4 0 0 1 0 8h-7M16 18l6 4"
      fill="none"
      stroke="#F4F5F7"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const glass = 'bg-white/[0.035] border border-white/[0.08] backdrop-blur-xl';

const ProjectCard = ({ project }) => (
  <article className={`flex flex-col gap-[22px] p-6 sm:p-8 rounded-3xl ${glass}`}>
    <div className="flex items-center justify-between gap-3">
      <div className="w-[52px] h-[52px] rounded-[14px] flex items-center justify-center bg-white/[0.06] border border-white/10 text-mint">
        {icons[project.icon]}
      </div>
      <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-mint/10 border border-mint/25 font-mono text-xs text-mint-soft">
        <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-mint" />
        Live
      </span>
    </div>
    <div>
      <h3 className="font-display font-semibold text-[26px] tracking-[-0.02em]">{project.name}</h3>
      <p className="mt-2.5 text-base leading-relaxed text-muted">{project.description}</p>
    </div>
    <ul className="flex flex-wrap gap-2">
      {project.features.map((feature) => (
        <li key={feature} className="px-3 py-1.5 rounded-full bg-white/5 text-[13px] text-subtle">
          {feature}
        </li>
      ))}
    </ul>
    <a
      href={projectUrl(project)}
      className="mt-auto flex items-center justify-between gap-3 min-h-[52px] px-5 rounded-[14px] bg-white/5 border border-white/10 font-semibold text-[15px] transition-colors hover:bg-white/10 hover:border-white/20"
    >
      <span>Open {project.shortName}</span>
      <span className="inline-flex items-center gap-2 font-mono font-medium text-[13px] text-faint">
        {project.subdomain}.
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M7 17L17 7M9 7h8v8" fill="none" stroke="#F4F5F7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    </a>
  </article>
);

const Homepage = () => {
  return (
    <div className="relative min-h-screen overflow-hidden bg-ink text-ink-text font-sans">
      {/* Background glows */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-[280px] left-1/2 -ml-[720px] w-[1100px] h-[760px] rounded-full bg-[radial-gradient(closest-side,rgba(125,249,194,0.16),rgba(125,249,194,0))]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-[120px] left-1/2 ml-10 w-[900px] h-[700px] rounded-full bg-[radial-gradient(closest-side,rgba(139,124,255,0.18),rgba(139,124,255,0))]"
      />

      {/* Header */}
      <header className="relative max-w-[1200px] mx-auto px-4 sm:px-6 pt-6">
        <nav
          aria-label="Primary"
          className="flex flex-wrap items-center justify-between gap-4 py-3 pr-3 pl-5 rounded-[18px] bg-white/[0.04] border border-white/[0.08] backdrop-blur-lg"
        >
          <a href="#top" className="flex items-center gap-3 min-h-[44px]">
            <Logo />
            <span className="font-display font-bold text-xl tracking-[-0.02em]">rosterfreak</span>
          </a>
          <div className="flex flex-wrap items-center gap-1">
            <a href="#projects" className="px-4 py-3 text-[15px] text-nav hover:text-white transition-colors">
              Projects
            </a>
            <a href="#contact" className="px-4 py-3 text-[15px] text-nav hover:text-white transition-colors">
              Contact
            </a>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="px-[18px] py-3 rounded-xl bg-mint text-ink font-semibold text-[15px] transition hover:brightness-110"
            >
              Get in touch
            </a>
          </div>
        </nav>
      </header>

      {/* Hero */}
      <section id="top" className="relative max-w-[1200px] mx-auto px-4 sm:px-6 pt-24 sm:pt-[120px] pb-20 sm:pb-[104px] text-center">
        <div className="inline-flex items-center gap-2.5 px-3.5 py-2 rounded-full bg-white/5 border border-white/10 font-mono text-[13px] tracking-[0.02em] text-subtle">
          <span aria-hidden="true" className="w-2 h-2 rounded-full bg-mint shadow-[0_0_12px_#7DF9C2]" />
          {projects.length} apps live now
        </div>
        <h1 className="mt-7 mx-auto max-w-[880px] font-display font-semibold text-[clamp(40px,7vw,76px)] leading-[1.05] tracking-[-0.035em]">
          Welcome to RosterFreak
        </h1>
        <p className="mt-6 mx-auto max-w-[560px] text-[19px] leading-relaxed text-muted">
          A collection of apps I’ve built and run, from fantasy leagues to whatever comes next.
        </p>
      </section>

      {/* Projects */}
      <section
        id="projects"
        aria-labelledby="projects-title"
        className="relative max-w-[1200px] mx-auto px-4 sm:px-6 pt-6 pb-24 sm:pb-[120px]"
      >
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <p className="font-mono text-[13px] tracking-[0.08em] uppercase text-mint">Projects</p>
            <h2 id="projects-title" className="mt-3 font-display font-semibold text-[clamp(32px,4vw,44px)] tracking-[-0.03em]">
              What’s live
            </h2>
          </div>
          <p className="max-w-[420px] text-base leading-relaxed text-muted">
            Each app runs on its own subdomain and gets updated on its own schedule.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {projects.map((project) => (
            <ProjectCard key={project.subdomain} project={project} />
          ))}
        </div>
      </section>

      {/* Contact */}
      <section id="contact" aria-labelledby="contact-title" className="relative max-w-[1200px] mx-auto px-4 sm:px-6 pb-24">
        <div className="relative overflow-hidden flex flex-wrap items-center justify-between gap-8 p-8 sm:p-14 rounded-[28px] bg-white/[0.04] border border-white/10">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-40 -top-[200px] w-[520px] h-[520px] rounded-full bg-[radial-gradient(closest-side,rgba(139,124,255,0.22),rgba(139,124,255,0))]"
          />
          <div className="relative flex-[1_1_420px]">
            <h2 id="contact-title" className="font-display font-semibold text-[clamp(28px,3.6vw,40px)] leading-[1.1] tracking-[-0.03em]">
              Got an idea?
            </h2>
            <p className="mt-3.5 max-w-[520px] text-[17px] leading-relaxed text-muted">
              Feature requests, collaborations or a brand-new app idea: send it over.
            </p>
          </div>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="relative inline-flex items-center gap-2.5 min-h-[56px] px-7 rounded-[14px] bg-mint text-ink font-semibold text-base transition hover:brightness-110"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="5" width="18" height="14" rx="3" />
              <path d="M4 7l8 6 8-6" />
            </svg>
            {CONTACT_EMAIL}
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative border-t border-white/[0.08]">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-7 flex flex-wrap items-center justify-between gap-4 text-sm text-faint">
          <span>© {new Date().getFullYear()} RosterFreak</span>
          <div className="flex flex-wrap gap-1">
            {projects.map((project) => (
              <a key={project.subdomain} href={projectUrl(project)} className="p-3 text-muted hover:text-white transition-colors">
                {project.shortName}
              </a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Homepage;
